import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerOverlay,
  Flex,
  Heading,
  Icon,
  IconButton,
  Text,
  VStack,
  useColorMode,
  useDisclosure,
  useBreakpointValue,
} from '@chakra-ui/react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  MdArrowBack,
  MdAgriculture,
  MdApartment,
  MdAssignment,
  MdDarkMode,
  MdDashboard,
  MdHistory,
  MdLightMode,
  MdLocationOn,
  MdMenu,
  MdPalette,
  MdPeople,
  MdManageAccounts,
  MdRefresh,
  MdSwapHoriz,
  MdTune,
} from 'react-icons/md';
import { useAuth } from '../../contexts/AuthContext';
import {
  AdminHeaderRefreshProvider,
  useAdminHeaderRefreshControls,
} from '../../contexts/AdminHeaderRefreshContext';
import { getPendingIndentCount } from '../../repositories/indent.repository';
import { listenForForegroundMessages } from '../../repositories/deviceTokens.repository';
import { useStaffAccess } from '../../utils/staffAccess';
import { getIndianClockParts } from '../../utils/datetime';
import { StatusBadge, BrandLogo } from '../../components/ui';

const SIDEBAR_W = '200px';
const PENDING_INDENT_POLL_MS = 150000;
const SIDEBAR_COLLAPSED_KEY = 'utkarsh_admin_sidebar_collapsed';

function readSidebarCollapsed() {
  try {
    return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === '1';
  } catch {
    return false;
  }
}

function HeaderClock() {
  const [parts, setParts] = useState(() => getIndianClockParts());

  useEffect(() => {
    const tick = () => setParts(getIndianClockParts());
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);

  if (!parts.day && !parts.date && !parts.time) return null;

  return (
    <Box
      textAlign={{ base: 'left', md: 'right' }}
      lineHeight="1.25"
      display={{ base: 'none', md: 'block' }}
      mr={{ md: 1 }}
    >
      <Text fontSize="13px" fontWeight="600" color="text.primary" whiteSpace="nowrap">
        {parts.day}
      </Text>
      <Text fontSize="12px" color="text.secondary" whiteSpace="nowrap">
        {parts.date}
        {parts.date && parts.time ? ' · ' : ''}
        {parts.time}
      </Text>
    </Box>
  );
}

function pageTitleFromPath(pathname) {
  const path = String(pathname || '').replace(/\/+$/, '') || '/admin';
  const rules = [
    [/^\/admin\/users\/[^/]+$/, 'User detail'],
    [/^\/admin\/users$/, 'Users'],
    [/^\/admin\/dashboard$/, 'Dashboard'],
    [/^\/admin\/theme$/, 'Theme'],
    [/^\/admin\/equipments\/fleet\/[^/]+$/, 'Equipment detail'],
    [/^\/admin\/equipments\/fleet$/, 'Equipment fleet'],
    [/^\/admin\/equipments\/indents\/[^/]+$/, 'Indent detail'],
    [/^\/admin\/equipments\/indents$/, 'Indents / Requests'],
    [/^\/admin\/equipments\/village-summary$/, 'Village'],
    [/^\/admin\/equipments\/bu-site-summary$/, 'Business Unit'],
    [/^\/admin\/equipments\/villagers\/[^/]+\/assets\/[^/]+$/, 'Equipment detail'],
    [/^\/admin\/equipments\/villagers\/[^/]+$/, 'Villager detail'],
    [/^\/admin\/equipments\/villagers$/, 'Villagers'],
    [/^\/admin\/equipments\/vendors\/[^/]+\/assets\/[^/]+$/, 'Equipment detail'],
    [/^\/admin\/equipments\/vendors\/[^/]+$/, 'Vendor detail'],
    [/^\/admin\/equipments\/vendors$/, 'Vendors'],
    [/^\/admin\/master-data\/villages$/, 'Villages'],
    [/^\/admin\/master-data\/business-units$/, 'Business Unit & Site'],
    [/^\/admin\/master-data\/equipments$/, 'Equipments Master'],
    [/^\/admin\/administration\/staff\/[^/]+$/, 'Staff detail'],
    [/^\/admin\/administration\/staff$/, 'Staff management'],
    [/^\/admin\/administration\/activities$/, 'System Logs'],
    [/^\/admin\/administration\/system-config$/, 'System configuration'],
  ];
  for (const [re, title] of rules) {
    if (re.test(path)) return title;
  }
  return 'Admin';
}

/** Parent list path for detail screens — shows Back in the top header. */
function headerBackFromPath(pathname) {
  const path = String(pathname || '').replace(/\/+$/, '') || '/admin';
  const rules = [
    [/^\/admin\/users\/[^/]+$/, '/admin/users'],
    [/^\/admin\/equipments\/fleet\/[^/]+$/, '/admin/equipments/fleet'],
    [/^\/admin\/equipments\/indents\/[^/]+$/, '/admin/equipments/indents'],
    [/^\/admin\/equipments\/villagers\/[^/]+\/assets\/[^/]+$/, '/admin/equipments/fleet'],
    [/^\/admin\/equipments\/villagers\/[^/]+$/, '/admin/equipments/villagers'],
    [/^\/admin\/equipments\/vendors\/[^/]+\/assets\/[^/]+$/, '/admin/equipments/vendors'],
    [/^\/admin\/equipments\/vendors\/[^/]+$/, '/admin/equipments/vendors'],
    [/^\/admin\/administration\/staff\/[^/]+$/, '/admin/administration/staff'],
  ];
  for (const [re, to] of rules) {
    if (re.test(path)) return to;
  }
  return null;
}

const mainNav = [
  { to: '/admin/dashboard', label: 'Dashboard', icon: MdDashboard },
];

const summaryNav = [
  {
    to: '/admin/equipments/village-summary',
    label: 'Village',
    icon: MdLocationOn,
  },
  {
    to: '/admin/equipments/bu-site-summary',
    label: 'Business Unit',
    icon: MdApartment,
  },
];

const themeNav = [{ to: '/admin/theme', label: 'Theme', icon: MdPalette }];

/** Fleet, requests, and members — day-to-day operations. */
const operationsNav = [
  {
    to: '/admin/equipments/fleet',
    label: 'Equipment fleet',
    icon: MdAgriculture,
  },
  {
    to: '/admin/equipments/indents',
    label: 'Indents Request',
    icon: MdAssignment,
  },
  { to: '/admin/users', label: 'Users', icon: MdPeople },
];

const administrationNavAll = [
  {
    to: '/admin/administration/staff',
    label: 'Staff',
    icon: MdManageAccounts,
    visibleKey: 'staff',
  },
  {
    to: '/admin/administration/activities',
    label: 'System Logs',
    icon: MdHistory,
    visibleKey: 'activities',
  },
  {
    to: '/admin/administration/system-config',
    label: 'System config',
    icon: MdTune,
    visibleKey: 'system',
  },
];

const masterDataNav = [
  { to: '/admin/master-data/villages', label: 'Villages', icon: MdLocationOn },
  {
    to: '/admin/master-data/business-units',
    label: 'Business Unit',
    icon: MdApartment,
  },
  {
    to: '/admin/master-data/equipments',
    label: 'Equipments',
    icon: MdAgriculture,
  },
];

function SidebarLink({ to, label, icon, badge, onNavigate }) {
  return (
    <NavLink
      to={to}
      style={{ textDecoration: 'none' }}
      onClick={() => onNavigate?.()}
    >
      {({ isActive }) => (
        <Flex
          align="center"
          gap={2.5}
          px={3}
          py={1.5}
          borderRadius="sm"
          bg={isActive ? 'utkarsh.amber' : 'transparent'}
          color={isActive ? 'text.onAmber' : 'whiteAlpha.900'}
          fontWeight={isActive ? '600' : '500'}
          fontSize="15px"
          lineHeight="1.25"
          _hover={{ bg: isActive ? 'utkarsh.amber' : 'whiteAlpha.100' }}
        >
          <Icon as={icon} boxSize={5} flexShrink={0} />
          <Text as="span" flex={1} noOfLines={1}>
            {label}
          </Text>
          {badge > 0 ? (
            <Flex
              minW="20px"
              h="20px"
              px={1}
              align="center"
              justify="center"
              borderRadius="full"
              bg={isActive ? 'utkarsh.ink' : 'utkarsh.amber'}
              color={isActive ? 'white' : 'text.onAmber'}
              fontSize="11px"
              fontWeight="700"
              lineHeight="1"
              flexShrink={0}
            >
              {badge > 99 ? '99+' : badge}
            </Flex>
          ) : null}
        </Flex>
      )}
    </NavLink>
  );
}

function NavSection({ title, items, onNavigate }) {
  return (
    <Box>
      {title ? (
        <Text
          fontSize="11px"
          fontWeight="700"
          letterSpacing="0.06em"
          textTransform="uppercase"
          color="whiteAlpha.500"
          px={3}
          mb={1}
          mt={3}
        >
          {title}
        </Text>
      ) : null}
      <VStack align="stretch" spacing={0.5}>
        {items.map((item) => (
          <SidebarLink key={item.to} {...item} onNavigate={onNavigate} />
        ))}
      </VStack>
    </Box>
  );
}

function SidebarNav({
  mainNavItems,
  summaryNavItems,
  operationsNavItems,
  administrationNav,
  masterDataNavItems,
  themeNavItems,
  onNavigate,
  onSwitchView,
  onGoHome,
}) {
  return (
    <>
      <Box
        as="button"
        type="button"
        textAlign="left"
        bg="transparent"
        border="none"
        p={0}
        mb={2.5}
        flexShrink={0}
        cursor="pointer"
        _hover={{ opacity: 0.85 }}
        onClick={() => {
          onGoHome?.();
          onNavigate?.();
        }}
        aria-label="Go to Classic home"
      >
        <Box
          bg="white"
          borderRadius="md"
          px={2}
          py={1.5}
          mb={1.5}
          display="inline-block"
          maxW="100%"
        >
          <BrandLogo variant="full" h="40px" maxW="156px" />
        </Box>
        <Text fontSize="12px" color="whiteAlpha.700">
          Classic view
        </Text>
      </Box>

      <Box flex="1" minH={0} overflowY="auto" pr={1}>
        <NavSection items={mainNavItems} onNavigate={onNavigate} />
        <NavSection title="Reports" items={summaryNavItems} onNavigate={onNavigate} />
        <NavSection title="Operations" items={operationsNavItems} onNavigate={onNavigate} />
        {administrationNav.length ? (
          <NavSection
            title="Administration"
            items={administrationNav}
            onNavigate={onNavigate}
          />
        ) : null}
        <NavSection title="Master Data" items={masterDataNavItems} onNavigate={onNavigate} />
        <Box mt={3}>
          <NavSection items={themeNavItems} onNavigate={onNavigate} />
        </Box>
      </Box>

      <Box mt={3} flexShrink={0} pt={2} borderTop="1px solid" borderColor="whiteAlpha.200">
        <Button
          w="100%"
          size="sm"
          justifyContent="flex-start"
          leftIcon={<Icon as={MdSwapHoriz} boxSize={5} />}
          bg="utkarsh.amber"
          color="text.onAmber"
          fontWeight="600"
          fontSize="13px"
          borderRadius="sm"
          _hover={{ bg: 'brand.600' }}
          onClick={() => {
            onSwitchView?.();
            onNavigate?.();
          }}
        >
          Switch to Hub view
        </Button>
      </Box>
    </>
  );
}

export default function AdminLayout() {
  return (
    <AdminHeaderRefreshProvider>
      <AdminLayoutShell />
    </AdminHeaderRefreshProvider>
  );
}

function AdminLayoutShell() {
  const { logout, staff } = useAuth();
  const {
    canAccessStaffManagement,
    canViewStaffActivities,
    canViewSystemConfig,
  } = useStaffAccess();
  const navigate = useNavigate();
  const location = useLocation();
  const pageTitle = pageTitleFromPath(location.pathname);
  const headerBackTo = headerBackFromPath(location.pathname);
  const { colorMode, toggleColorMode } = useColorMode();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const headerRefresh = useAdminHeaderRefreshControls();
  const [pendingIndents, setPendingIndents] = useState(0);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(readSidebarCollapsed);
  const isMobileNav = useBreakpointValue({ base: true, lg: false });

  const toggleSidebar = () => {
    setSidebarCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(SIDEBAR_COLLAPSED_KEY, next ? '1' : '0');
      } catch {
        // ignore
      }
      return next;
    });
  };

  const handleSwitchView = () => {
    navigate('/admin/hub');
  };

  useEffect(() => {
    let cancelled = false;
    const loadPending = async () => {
      try {
        const { pending } = await getPendingIndentCount();
        if (!cancelled) setPendingIndents(pending);
      } catch {
        // Keep the last known count; polling should stay quiet.
      }
    };
    loadPending();
    const timer = setInterval(loadPending, PENDING_INDENT_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, []);

  useEffect(() => {
    let unsubscribe = () => {};
    let cancelled = false;
    (async () => {
      const stop = await listenForForegroundMessages();
      if (cancelled) {
        if (typeof stop === 'function') stop();
        return;
      }
      if (typeof stop === 'function') unsubscribe = stop;
    })();
    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, []);

  const displayName = staff?.fullName || staff?.name || staff?.email || 'Staff';
  const displayCode = staff?.staffId || staff?.code || '';

  const administrationNav = useMemo(() => {
    const visibility = {
      staff: canAccessStaffManagement,
      activities: canViewStaffActivities,
      system: canViewSystemConfig,
    };
    return administrationNavAll.filter((item) => visibility[item.visibleKey]?.());
  }, [canAccessStaffManagement, canViewStaffActivities, canViewSystemConfig]);

  const operationsNavItems = useMemo(
    () =>
      operationsNav.map((item) =>
        item.to === '/admin/equipments/indents'
          ? { ...item, badge: pendingIndents }
          : item,
      ),
    [pendingIndents],
  );

  const handleLogout = async () => {
    await logout();
    navigate('/auth/sign-in');
  };

  const navProps = {
    mainNavItems: mainNav,
    summaryNavItems: summaryNav,
    operationsNavItems,
    administrationNav,
    masterDataNavItems: masterDataNav,
    themeNavItems: themeNav,
    onSwitchView: handleSwitchView,
    onGoHome: () => navigate('/admin/dashboard'),
  };

  const showDesktopSidebar = !sidebarCollapsed;

  return (
    <Flex h="100vh" overflow="hidden" bg="bg.canvas">
      <Box
        as="aside"
        w={SIDEBAR_W}
        minW={SIDEBAR_W}
        h="100vh"
        position="sticky"
        top={0}
        bg="bg.sidebar"
        color="white"
        px={2.5}
        py={3.5}
        display={{ base: 'none', lg: showDesktopSidebar ? 'flex' : 'none' }}
        flexDirection="column"
        flexShrink={0}
        overflow="hidden"
      >
        <SidebarNav {...navProps} />
      </Box>

      <Drawer isOpen={isOpen} placement="left" onClose={onClose}>
        <DrawerOverlay />
        <DrawerContent bg="bg.sidebar" color="white" maxW={SIDEBAR_W}>
          <DrawerCloseButton color="whiteAlpha.800" />
          <DrawerBody px={2.5} py={3.5} display="flex" flexDirection="column">
            <SidebarNav {...navProps} onNavigate={onClose} />
          </DrawerBody>
        </DrawerContent>
      </Drawer>

      <Flex direction="column" flex="1" minW={0} h="100vh" overflow="hidden">
        <Flex
          as="header"
          align="center"
          justify="space-between"
          px={{ base: 3, md: 6 }}
          py={3}
          borderBottom="1px solid"
          borderColor="border.subtle"
          bg="bg.surface"
          flexShrink={0}
          gap={3}
        >
          <Flex align="center" gap={2} minW={0}>
            <IconButton
              aria-label={sidebarCollapsed || isMobileNav ? 'Open menu' : 'Hide menu'}
              icon={<MdMenu />}
              onClick={() => {
                if (isMobileNav) onOpen();
                else toggleSidebar();
              }}
              variant="ghostInk"
              size="sm"
              display="inline-flex"
            />
            {headerBackTo ? (
              <Button
                size="sm"
                variant="outlineInk"
                leftIcon={<MdArrowBack />}
                onClick={() => navigate(headerBackTo)}
                flexShrink={0}
              >
                Back
              </Button>
            ) : null}
            <Text fontWeight="600" color="text.primary" fontSize="14px" noOfLines={1}>
              {pageTitle}
            </Text>
            {location.pathname === '/admin/equipments/indents' ||
            location.pathname.startsWith('/admin/equipments/indents/') ? (
              <StatusBadge status={pendingIndents > 0 ? 'pending' : 'neutral'}>
                {pendingIndents} pending
              </StatusBadge>
            ) : null}
          </Flex>
          <Flex align="center" gap={{ base: 2, md: 3 }} flexShrink={0}>
            <HeaderClock />
            <Button
              size="sm"
              variant="outlineInk"
              leftIcon={<MdRefresh />}
              onClick={headerRefresh?.runRefresh}
              isLoading={headerRefresh?.refreshing}
            >
              Refresh
            </Button>
            <Box textAlign="right" lineHeight="1.2" display={{ base: 'none', sm: 'block' }}>
              <Text fontSize="13px" fontWeight="600" color="text.primary" noOfLines={1}>
                {displayName}
              </Text>
              {displayCode ? (
                <Text fontSize="11px" color="text.secondary">
                  {displayCode}
                </Text>
              ) : null}
            </Box>
            <IconButton
              aria-label="Toggle color mode"
              icon={colorMode === 'light' ? <MdDarkMode /> : <MdLightMode />}
              onClick={toggleColorMode}
              variant="ghostInk"
              size="sm"
            />
            <Button size="sm" variant="outlineInk" onClick={handleLogout}>
              Logout
            </Button>
          </Flex>
        </Flex>
        <Box
          as="main"
          flex="1"
          minH={0}
          overflowY="auto"
          bg="bg.canvas"
          px={{ base: 3, md: 6 }}
          py={{ base: 4, md: 6 }}
          w="100%"
        >
          <Outlet />
        </Box>
      </Flex>
    </Flex>
  );
}
